import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";

interface Toast {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message?: string;
  duration?: number;
}

interface ToastContextType {
  showToast: (toast: Omit<Toast, "id">) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = {
      id,
      duration: 8000,
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);

    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        hideToast(id);
      }, newToast.duration);
    }
  }, [hideToast]);

  const getIcon = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return (
          <svg
            className="w-5 h-5 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "error":
        return (
          <svg
            className="w-5 h-5 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "warning":
        return (
          <svg
            className="w-5 h-5 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "info":
        return (
          <svg
            className="w-5 h-5 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
    }
  };

  const getColors = (type: Toast["type"]) => {
    switch (type) {
      case "success":
        return "bg-green-600/10 backdrop-blur-sm border-green-500/40 shadow-[0_0_20px_rgba(34,197,94,0.15)]";
      case "error":
        return "bg-red-600/10 backdrop-blur-sm border-red-500/40 shadow-[0_0_20px_rgba(220,38,38,0.15)]";
      case "warning":
        return "bg-yellow-600/10 backdrop-blur-sm border-yellow-500/40 shadow-[0_0_20px_rgba(234,179,8,0.15)]";
      case "info":
        return "bg-blue-600/10 backdrop-blur-sm border-blue-500/40 shadow-[0_0_20px_rgba(59,130,246,0.15)]";
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 left-4 sm:left-auto z-50 space-y-2">
        {toasts.map((toast) => {
          // Determine toast width based on message length and type
          const isLongMessage = toast.message && toast.message.length > 100;
          const isContentPolicyError =
            toast.title === "Content Policy Violation" ||
            (toast.message && toast.message.includes("sensitive words"));
          const isErrorType = toast.type === "error";

          let toastWidth = "w-full sm:w-96"; // Full width on mobile, 384px on desktop
          if (isContentPolicyError) {
            toastWidth = "w-full sm:w-[28rem]"; // Full width on mobile, 448px on desktop for policy errors
          } else if (isLongMessage && isErrorType) {
            toastWidth = "w-full sm:w-[26rem]"; // Full width on mobile, 416px on desktop
          }

          return (
            <div
              key={toast.id}
              className={`
                ${toastWidth} shadow-lg rounded-xl border-2 pointer-events-auto 
                ${getColors(toast.type)}
                animate-in slide-in-from-right duration-300
              `}
            >
              <div className="p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">{getIcon(toast.type)}</div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">
                      {toast.title}
                    </p>
                    {toast.message && (
                      <p
                        className={`mt-1 text-sm text-white/70 break-words ${
                          isContentPolicyError || isLongMessage
                            ? "leading-relaxed"
                            : ""
                        }`}
                      >
                        {toast.message}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex-shrink-0 flex">
                    <button
                      onClick={() => hideToast(toast.id)}
                      className="inline-flex text-white/70 hover:text-white focus:outline-none transition-colors"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};
